import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { clearAnalyticsStorage } from "../services/analytics";
import {
  CONSENT_BANNER_VERSION,
  CONSENT_MAX_AGE_MONTHS,
  CONSENT_POLICY_VERSION,
  CONSENT_STORAGE_KEY,
  defaultConsentCategories,
} from "../data/privacy";

const ConsentContext = createContext(null);

function createConsentId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `consent-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function readStoredReceipt() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(CONSENT_STORAGE_KEY));
    const expiresAt = Date.parse(stored?.expiresAt);
    if (
      !stored ||
      stored.policyVersion !== CONSENT_POLICY_VERSION ||
      !Number.isFinite(expiresAt) ||
      expiresAt <= Date.now() ||
      typeof stored.categories?.audienceMeasurement !== "boolean" ||
      typeof stored.categories?.experienceAnalytics !== "boolean"
    ) {
      window.localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}

function addCalendarMonths(date, months) {
  const result = new Date(date);
  const originalDay = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const lastDayOfTargetMonth = new Date(
    result.getFullYear(),
    result.getMonth() + 1,
    0
  ).getDate();
  result.setDate(Math.min(originalDay, lastDayOfTargetMonth));
  return result;
}

export function ConsentProvider({ children }) {
  const [receipt, setReceipt] = useState(readStoredReceipt);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const categories = receipt?.categories || defaultConsentCategories;
  const hasDecision = Boolean(receipt);

  const storeChoice = useCallback(
    (nextCategories, language, action) => {
      const normalized = {
        necessary: true,
        audienceMeasurement: Boolean(nextCategories.audienceMeasurement),
        experienceAnalytics: Boolean(nextCategories.experienceAnalytics),
      };
      const revoked =
        (categories.audienceMeasurement && !normalized.audienceMeasurement) ||
        (categories.experienceAnalytics && !normalized.experienceAnalytics);
      const timestamp = new Date();
      const nextReceipt = {
        consentId: receipt?.consentId || createConsentId(),
        timestamp: timestamp.toISOString(),
        expiresAt: addCalendarMonths(timestamp, CONSENT_MAX_AGE_MONTHS).toISOString(),
        policyVersion: CONSENT_POLICY_VERSION,
        bannerVersion: CONSENT_BANNER_VERSION,
        language,
        action,
        categories: normalized,
      };

      try {
        window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(nextReceipt));
      } catch {
        // The choice remains active for this page even if storage is unavailable.
      }

      if (revoked) clearAnalyticsStorage();
      setReceipt(nextReceipt);
      setPreferencesOpen(false);

      // A reload fully removes already-executed third-party code after a revocation.
      if (revoked) window.location.reload();
    },
    [categories.audienceMeasurement, categories.experienceAnalytics, receipt?.consentId]
  );

  const acceptAll = useCallback(
    (language) =>
      storeChoice(
        { audienceMeasurement: true, experienceAnalytics: true },
        language,
        "accept-all"
      ),
    [storeChoice]
  );

  const rejectAll = useCallback(
    (language) =>
      storeChoice(
        { audienceMeasurement: false, experienceAnalytics: false },
        language,
        "reject-non-essential"
      ),
    [storeChoice]
  );

  const savePreferences = useCallback(
    (nextCategories, language) =>
      storeChoice(nextCategories, language, "save-selection"),
    [storeChoice]
  );

  const value = useMemo(
    () => ({
      categories,
      hasDecision,
      preferencesOpen,
      openPreferences: () => setPreferencesOpen(true),
      closePreferences: () => setPreferencesOpen(false),
      acceptAll,
      rejectAll,
      savePreferences,
    }),
    [
      acceptAll,
      categories,
      hasDecision,
      preferencesOpen,
      rejectAll,
      savePreferences,
    ]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const context = useContext(ConsentContext);
  if (!context) throw new Error("useConsent must be used within ConsentProvider");
  return context;
}
